import {isEqual} from 'lodash';
import memoizeOne from 'memoize-one';
import {useEffect, useMemo} from 'react';

import {
  createUserActivity,
  HandoffActivityTypesEnum,
  RestorationData,
} from '../../../../srcCommon/app/handoff/handoffModel';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {
  invalidateCurrentUserActivityAsync,
  setUserActivityAsync,
} from '../../../services/electron/electronBridgeOutbound';
import {getUserId} from '../../../store/data/teammates/teammatesSelectors';

export function useDesktopUserActivity<T extends HandoffActivityTypesEnum>(
  type: T,
  userInfo: RestorationData<T>,
  isDisabled: boolean,
) {
  const userId = useAppSelector(getUserId);
  const userActivity = useStableEnabledUserActivity(userId, type, userInfo, isDisabled);

  useEffect(() => {
    if (!userActivity) {
      return () => {};
    }

    setUserActivityAsync(userActivity);

    return () => {
      invalidateCurrentUserActivityAsync();
    };
  }, [userActivity]);
}

function useStableEnabledUserActivity<T extends HandoffActivityTypesEnum>(
  userId: number,
  type: T,
  userInfo: RestorationData<T>,
  isDisabled: boolean,
) {
  const createStableUserActivity = useMemo(() => memoizeOne(createUserActivity, isEqual), []);

  if (isDisabled) {
    return undefined;
  }

  return createStableUserActivity(type, userInfo, userId);
}
